import { useConfirmDialog } from '@if/mui'
import { useReplayCalendarActions } from '@src/api/loan/loan-api'
import { SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/**
 * A simulation expires while the agent works in it, and the pending actions were computed
 * against loan data that has since moved. Confirming recomputes them server-side against
 * fresh data and hands the result back through `onRefreshed` — nothing queued is lost, it
 * is only re-priced. Cancelling leaves the calendar exactly as it was, still expired.
 *
 * It replays the whole stack, which is the same call the change log makes with a prefix of
 * it to undo.
 */
export const useStaleSimulationDialog = (
  currentSimulation: SignedCalendarSimulation,
  onRefreshed: (refreshed: SignedCalendarSimulation) => void,
) => {
  const { t } = useTranslation()
  const { loan } = currentSimulation.simulation.fullLoan
  const { mutateAsync: replayActions } = useReplayCalendarActions(loan.id, loan.financingCompanyId)

  return useConfirmDialog({
    icon: RefreshCw,
    title: t('loan.calendar.staleSimulation.title'),
    message: t('loan.calendar.staleSimulation.message'),
    confirmLabel: t('loan.calendar.staleSimulation.refresh'),
    cancelLabel: t('loan.calendar.staleSimulation.cancel'),
    onConfirm: async () => {
      const { simulation } = currentSimulation
      onRefreshed(await replayActions({ simulation, actions: simulation.actions }))
    },
  })
}
